import {observer} from "mobx-react";
import React, {useEffect, useRef, useState} from "react";
import Store from "../store";
import ErrorMessage from "./error";
import {useFilingStripe} from './public-filing-gate';
import {AddressElement, Elements, useElements, useStripe} from "@stripe/react-stripe-js";
import {PaymentElement} from '@stripe/react-stripe-js';
import Loading from "./loading";
import type * as stripeJs from "@stripe/stripe-js";
import {Alert, Box, Checkbox, Container, FormControlLabel} from "@mui/material";
import {DarkButton} from "./buttons";
import {FortressDivider} from "./fortress-divider";
import {paymentAppearance} from '../assets/payment-appearance';
import RenewalStore from "../stores/renewal-store";
import {recordPaymentAuthorization} from "../assets/paymentAuthorization";
import {PreorderSchedule, prepareRenewalCheckout, RenewalCheckout} from '../assets/renewal-checkout';
import {RenewalPaymentSummary} from './renewal-payment-summary';
import {PaymentDetailsHeading} from './payment-details-heading';


const RenewStepTwo = observer(({companyInfo}: {companyInfo: any}) => {
    const stripePromise = useFilingStripe();
    const [errorMessage, setErrorMessage] = useState('');
    const [checkout, setCheckout] = useState<(RenewalCheckout & {signature: string}) | null>(null);
    const [retry, setRetry] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const submittingRef = useRef(false);
    const signature = Store.getSignatureHash;
    const certificateOfStatus = RenewalStore.certificateOfStatus;
    const currentCheckout = checkout?.signature === signature && checkout.certificateOfStatus === certificateOfStatus ? checkout : null;
    useEffect(() => {
        if (!RenewalStore.hasValidSignature) {
            Store.setRenewStep(1);
            return;
        }
        let active = true;
        setCheckout(null);
        setErrorMessage('');
        prepareRenewalCheckout(signature, JSON.parse(JSON.stringify(RenewalStore.getUpdatedCompanyInfo)), certificateOfStatus)
            .then(result => {
                if (active) setCheckout({...result, signature});
            }).catch(error => {
                if (active) setErrorMessage(error instanceof Error ? error.message : 'Checkout could not be loaded. Please retry.');
            });
        return () => { active = false; };
    }, [companyInfo.id, signature, certificateOfStatus, retry]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const options: stripeJs.StripeElementsOptions | undefined = currentCheckout ? {
        clientSecret: currentCheckout.clientSecret,
        appearance: paymentAppearance,
    } : undefined;

    return <>
        <Box sx={{mt: 3}}>
            <PaymentDetailsHeading description={`${currentCheckout?.targetYear ? `${currentCheckout.targetYear} ` : ''}Annual Report - ${companyInfo.name} (${companyInfo.id})`}/>
        </Box>
        <RenewalPaymentSummary quote={currentCheckout?.quote ?? null} certificateFee={companyInfo.certificateOfStatusFee}
                               certificateOfStatus={certificateOfStatus} disabled={submitting || !currentCheckout}
                               onCertificateChange={selected => {
                                   if (submittingRef.current) return;
                                   setCheckout(null);
                                   RenewalStore.setCertificateOfStatus(selected);
                               }}/>
        {currentCheckout ? <Elements key={currentCheckout.clientSecret} stripe={stripePromise} options={options}>
            <CheckoutForm clientSecret={currentCheckout.clientSecret} authorizationText={currentCheckout.authorizationText}
                          authorizationVersion={currentCheckout.authorizationVersion}
                          mode={currentCheckout.mode} preorder={currentCheckout.preorder} onSubmitting={value => {
                              submittingRef.current = value;
                              setSubmitting(value);
                          }}/>
        </Elements> : errorMessage ? <>
            <ErrorMessage message={errorMessage}/>
            <DarkButton onClick={() => setRetry(value => value + 1)}>Retry checkout</DarkButton>
            <DarkButton onClick={() => Store.setRenewStep(1)}>Back to company details</DarkButton>
        </> : <Loading/>}
    </>;
});

const CheckoutForm = ({clientSecret, authorizationText, authorizationVersion, mode, preorder, onSubmitting}: {
    clientSecret: string; authorizationText: string; authorizationVersion: string;
    mode: 'payment' | 'setup'; preorder: PreorderSchedule | null;
    onSubmitting: (submitting: boolean) => void;
}) => {
    const stripe = useStripe();
    const elements = useElements();
    const [chargeAccepted, setChargeAccepted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const inFlight = useRef(false);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!stripe || !elements || !chargeAccepted || inFlight.current || !RenewalStore.hasValidSignature) return;
        inFlight.current = true;
        setSubmitting(true);
        onSubmitting(true);
        setErrorMessage('');
        try {
            await recordPaymentAuthorization(clientSecret, authorizationText, authorizationVersion);
            if (!RenewalStore.hasValidSignature) throw new Error('Company details changed. Please review and sign again.');
            // An early (pre-order) filing only saves the card; it is charged when the state opens filing in January.
            const result = mode === 'setup'
                ? await stripe.confirmSetup({elements, confirmParams: {return_url: `${window.location.origin}/complete`}})
                : await stripe.confirmPayment({elements, confirmParams: {return_url: `${window.location.origin}/complete`}});
            if (result.error) throw new Error(result.error.message ?? (mode === 'setup' ? 'Your card could not be saved.' : 'Payment could not be confirmed.'));
            Store.setRenewStep(3);
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Payment could not be confirmed. Please try again.');
            inFlight.current = false;
            setSubmitting(false);
            onSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            {mode === 'setup' && preorder ? <Alert severity="info" sx={{mt: 2}}>
                <strong>Your card will be saved, not charged today.</strong> Florida opens {preorder.year} annual report filing on {preorder.opensOn}.
                We'll process your filing between {preorder.window}, and your card will be charged at that time.
            </Alert> : null}
            <FortressDivider textAlign="left" sx={{m: 2}}>Billing Information</FortressDivider>
            <PaymentElement options={{fields: {billingDetails: {name: 'auto', email: 'auto', address: 'auto'}}}}/>
            <FortressDivider textAlign="left" sx={{m: 2}}>Billing Address</FortressDivider>
            <AddressElement options={{mode: 'billing'}}/>
            <FormControlLabel
                control={<Checkbox checked={chargeAccepted} disabled={submitting} onChange={event => setChargeAccepted(event.target.checked)}/>}
                label={authorizationText} sx={{mt: 3}}
            />
            <Container maxWidth="sm" sx={{display: 'flex', flexDirection: 'column'}}>
                <DarkButton type="submit" disabled={!stripe || !chargeAccepted || submitting} sx={{p: 2, mt: 3}} disableElevation variant="contained">
                    {submitting ? 'Submitting...' : mode === 'setup' ? 'Save Card & Schedule My Filing' : 'Submit Payment'}
                </DarkButton>
                <DarkButton type="button" disabled={submitting} onClick={() => Store.setRenewStep(1)} sx={{mt: 2}}>
                    Back to company details
                </DarkButton>
            </Container>
            <ErrorMessage message={errorMessage}/>
        </form>
    );
};

export default RenewStepTwo;
